/* Service worker registration + deploy detection.
   The worker filename carries a build hash, so a redeploy is detected by polling the
   version manifest — never by silently reloading the page under the user. */

import { flushAllDrafts } from "@/lib/drafts";

const MANIFEST = "/sw-version.json";
const POLL_MS = 30000;
const VERSION_KEY = "cx_sw_version";
const DISMISSED_KEY = "cx_update_dismissed";

let registeredVersion = null;
let pending = null; // { version, file }
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn(pending));

/** Subscribe to "a new deployment is live". Fires immediately if one is already pending. */
export const onUpdateAvailable = (fn) => {
  listeners.add(fn);
  if (pending) fn(pending);
  return () => listeners.delete(fn);
};

export const dismissUpdate = (version) => {
  localStorage.setItem(DISMISSED_KEY, version);
  pending = null;
  emit();
};

/** Saves every visible form field so nothing typed is lost across the reload. */
export const snapshotForms = () => {
  try {
    const fields = {};
    document.querySelectorAll("input, textarea, select").forEach((el, i) => {
      if (el.type === "password" || el.type === "file" || !el.value) return;
      const key = el.getAttribute("data-testid") || el.name || el.id || `field-${i}`;
      fields[key] = el.type === "checkbox" || el.type === "radio" ? el.checked : el.value;
    });
    if (Object.keys(fields).length) {
      localStorage.setItem(
        "cx_form_snapshot",
        JSON.stringify({ path: window.location.pathname, at: new Date().toISOString(), fields })
      );
    }
  } catch (e) {
    /* a snapshot failure must never block the reload */
  }
};

/** Activates the new build: snapshot, hand over to the new worker, then reload. */
export const applyUpdate = async () => {
  flushAllDrafts();
  snapshotForms();
  try {
    if (pending?.file && "serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.register(pending.file, { updateViaCache: "none" });
      if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
      localStorage.setItem(VERSION_KEY, pending.version);
    }
  } catch (e) {
    /* fall through — a plain reload still picks up the new build */
  }
  window.location.reload();
};

const readManifest = async () => {
  const res = await fetch(MANIFEST, { cache: "no-store" });
  if (!res.ok) throw new Error("no manifest");
  return res.json();
};

const check = async () => {
  try {
    const { version, file } = await readManifest();
    if (!version || version === registeredVersion) return;
    if (localStorage.getItem(DISMISSED_KEY) === version) return;
    pending = { version, file };
    emit();
  } catch (e) {
    /* offline or the manifest is missing — try again on the next tick */
  }
};

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  try {
    const { version, file } = await readManifest().catch(() => ({ version: null, file: "/sw.js" }));
    registeredVersion = version;
    if (version) localStorage.setItem(VERSION_KEY, version);
    const reg = await navigator.serviceWorker.register(file, { updateViaCache: "none" });
    reg.update().catch(() => {});
  } catch (e) {
    return; // the app works fine without the offline shell
  }

  setInterval(check, POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}
